import React, { useState, useCallback } from 'react';
import { formatHelpers } from '@/utils/helpers';
import {
  PaymentChannelType,
  ChannelValue,
  GenerateLinkData
} from '@/services/types';

// Hooks
import { useRechargeConfig } from '@/hooks/useRechargeConfig';
import { useGenerateLink } from '@/hooks/useGenerateLink';
import { useLocalStorage } from '@/hooks/useLocalStorage';

// Layout Components
import { Header } from '@/components/Layout/Header';
import { PageContainer, SectionContainer } from '@/components/Layout/Container';

// UI Components
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';

// Business Components
import { SimplePackageSelector } from '@/components/RechargePackages/SimplePackageSelector';
import { SimplePaymentSelector } from '@/components/PaymentChannels/SimplePaymentSelector';

// 应用状态接口
interface AppState {
  selectedPackageId: string | null;
  selectedChannelType: PaymentChannelType | null;
  selectedChannel: ChannelValue | null;
  generatedLink: GenerateLinkData | null;
}

// 默认状态
const defaultState: AppState = {
  selectedPackageId: null,
  selectedChannelType: null,
  selectedChannel: null,
  generatedLink: null
};

function App() {
  // 状态管理
  const [appState, setAppState] = useState<AppState>(defaultState);
  
  // 本地存储
  const { value: savedState, setValue: setSavedState } = useLocalStorage<Partial<AppState>>(
    'last_selected_channel',
    { defaultValue: {} }
  );

  // API Hooks
  const { 
    data: rechargeConfig, 
    loading: configLoading, 
    error: configError,
    refresh: refreshConfig 
  } = useRechargeConfig();

  const {
    data: linkData,
    loading: linkLoading,
    error: linkError,
    generateLink,
    reset: resetLink
  } = useGenerateLink({
    onSuccess: (data) => {
      setAppState(prev => ({ ...prev, generatedLink: data }));
      window.open(data.link, '_blank');
    },
    onError: (error) => {
      console.error('生成支付链接失败:', error);
    }
  });

  // 获取当前选中的套餐
  const selectedPackage = rechargeConfig?.recharge_configs.find(
    pkg => pkg.recharge_product_id === appState.selectedPackageId
  ) || null;

  // 处理套餐选择
  const handlePackageSelect = useCallback((packageId: string) => {
    const newState = {
      ...appState,
      selectedPackageId: packageId,
      selectedChannelType: null,
      selectedChannel: null,
      generatedLink: null
    };
    
    setAppState(newState);
    setSavedState({ selectedPackageId: packageId });
    resetLink();
  }, [appState, setSavedState, resetLink]);

  // 处理支付类型选择
  const handleTypeSelect = useCallback((type: PaymentChannelType) => {
    // 如果传入空字符串，重置类型
    if (!type) {
      const newState = {
        ...appState,
        selectedChannelType: null,
        selectedChannel: null,
        generatedLink: null
      };
      
      setAppState(newState);
      setSavedState({ 
        selectedPackageId: appState.selectedPackageId,
        selectedChannelType: null,
        selectedChannel: null
      });
      resetLink();
      return;
    }

    const newState = {
      ...appState,
      selectedChannelType: type,
      selectedChannel: null,
      generatedLink: null
    };
    
    setAppState(newState);
    setSavedState({ 
      selectedPackageId: appState.selectedPackageId,
      selectedChannelType: type,
      selectedChannel: null
    });
    resetLink();
  }, [appState, setSavedState, resetLink]);

  // 处理支付渠道选择
  const handleChannelSelect = useCallback((channel: ChannelValue) => {
    // 如果传入空字符串，重置渠道
    if (!channel) {
      const newState = {
        ...appState,
        selectedChannel: null,
        generatedLink: null
      };
      
      setAppState(newState);
      setSavedState({ 
        selectedPackageId: appState.selectedPackageId,
        selectedChannelType: appState.selectedChannelType,
        selectedChannel: null
      });
      resetLink();
      return;
    }

    const newState = {
      ...appState,
      selectedChannel: channel,
      generatedLink: null
    };
    
    setAppState(newState);
    setSavedState({ 
      selectedPackageId: appState.selectedPackageId,
      selectedChannelType: appState.selectedChannelType,
      selectedChannel: channel
    });
    resetLink();
  }, [appState, setSavedState, resetLink]);

  // 生成支付链接
  const handleGenerateLink = useCallback(async () => {
    if (!appState.selectedPackageId || !appState.selectedChannelType || !appState.selectedChannel) {
      return;
    }

    await generateLink({
      recharge_id: appState.selectedPackageId,
      type: appState.selectedChannelType,
      channel: appState.selectedChannel
    });
  }, [appState, generateLink]);

  // 复制链接到剪贴板
  const handleCopyLink = useCallback(async () => {
    if (!appState.generatedLink?.link) return;

    try {
      await navigator.clipboard.writeText(appState.generatedLink.link);
      // 这里可以添加成功提示
    } catch (error) {
      console.error('复制失败:', error);
      // 这里可以添加失败提示
    }
  }, [appState.generatedLink]);

  // 重置所有状态
  // const handleReset = useCallback(() => {
  //   setAppState(defaultState);
  //   setSavedState({});
  //   resetLink();
  // }, [setSavedState, resetLink]);

  // 初始化时恢复保存的状态
  React.useEffect(() => {
    if (savedState.selectedPackageId) {
      setAppState(prev => ({
        ...prev,
        selectedPackageId: savedState.selectedPackageId || null,
        selectedChannelType: savedState.selectedChannelType || null,
        selectedChannel: savedState.selectedChannel || null
      }));
    }
  }, [savedState]);

  // 检查是否可以生成链接
  const canGenerateLink = Boolean(
    appState.selectedPackageId && 
    appState.selectedChannelType && 
    appState.selectedChannel &&
    !linkLoading
  );

  return (
    <div className="min-h-screen bg-fluent-primary">
      <PageContainer
        header={
          <Header
            title="充值调试"
            subtitle=""
            transparent={false}
            actions={
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={refreshConfig}
                  disabled={configLoading}
                  loading={configLoading}
                >
                  刷新
                </Button>
                {/* <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                >
                  重置
                </Button> */}
              </div>
            }
          />
        }
        loading={configLoading && !rechargeConfig}
        error={configError && (
          <Alert
            variant="error"
            title="加载配置失败"
            description={configError.message}
            className="glass-card animate-slide-down"
          />
        )}
      >
        <SectionContainer spacing="sm" className="space-y-4">
          {/* 单个紧凑的表单卡片 */}
          <Card className="p-4">
            <div className="space-y-4">
              
              {/* 充值套餐选择 */}
              <SimplePackageSelector
                packages={rechargeConfig?.recharge_configs || []}
                selectedPackageId={appState.selectedPackageId || undefined}
                onSelect={handlePackageSelect}
                loading={configLoading}
              />

              {/* 支付方式选择 */}
              <SimplePaymentSelector
                channels={rechargeConfig?.channels}
                selectedType={appState.selectedChannelType || undefined}
                selectedChannel={appState.selectedChannel || undefined}
                onTypeSelect={handleTypeSelect}
                onChannelSelect={handleChannelSelect}
                disabled={configLoading}
              />

              {/* 当前选择摘要 - 仅在有选择时显示 */}
              {(selectedPackage || appState.selectedChannelType) && (
                <div className="bg-gray-50 rounded-md p-3 text-sm">
                  {selectedPackage && (
                    <div className="mb-1">
                      <span className="text-gray-600">套餐: </span>
                      <span className="font-medium">
                        {selectedPackage.recharge_product_name} - {formatHelpers.formatPrice(selectedPackage.cny_price)}
                      </span>
                    </div>
                  )}
                  {appState.selectedChannelType && appState.selectedChannel && (
                    <div>
                      <span className="text-gray-600">支付: </span>
                      <span className="font-medium">
                        {appState.selectedChannelType}.{appState.selectedChannel}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* 生成按钮 */}
              <Button
                variant="primary"
                size="md"
                fullWidth
                onClick={handleGenerateLink}
                disabled={!canGenerateLink}
                loading={linkLoading}
              >
                {linkLoading ? '生成中...' : '生成支付链接'}
              </Button>

              {/* 错误提示 */}
              {linkError && (
                <Alert
                  variant="error"
                  title="生成失败"
                  description={linkError.message}
                  closable={true}
                  onClose={resetLink}
                />
              )}

              {/* 生成结果 */}
              {appState.generatedLink && (
                <div className="border-2 border-green-200 bg-green-50 rounded-md p-3">
                  <h3 className="text-base font-semibold text-green-800 mb-2">
                    生成成功
                  </h3>
                  
                  <div className="space-y-2">
                    {/* 支付链接 */}
                    <div>
                      <label className="block text-xs font-medium text-green-700 mb-1">
                        支付链接
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={appState.generatedLink.link}
                          readOnly
                          className="flex-1 px-2 py-1 text-xs bg-white border border-green-300 rounded font-mono"
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleCopyLink}
                        >
                          复制
                        </Button>
                      </div>
                    </div>

                    {/* 订单信息 - 紧凑显示 */}
                    {(appState.generatedLink.order_id || appState.generatedLink.expire_time) && (
                      <div className="text-xs space-y-1">
                        {appState.generatedLink.order_id && (
                          <div>
                            <span className="text-green-700 font-medium">订单ID: </span>
                            <span className="font-mono text-gray-700">{appState.generatedLink.order_id}</span>
                          </div>
                        )}
                        {appState.generatedLink.expire_time && (
                          <div>
                            <span className="text-green-700 font-medium">过期: </span>
                            <span className="text-gray-700">
                              {formatHelpers.formatTimestamp(appState.generatedLink.expire_time)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 操作按钮 */}
                    <div className="flex gap-2 pt-1">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.open(appState.generatedLink!.link, '_blank')}
                        className="flex-1"
                      >
                        打开支付页面
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={resetLink}
                      >
                        清除
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

        {/* 调试信息 */}
        {import.meta.env.DEV && (
          <section>
            <Card className="p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">调试信息</h3>
              <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded overflow-auto">
                {JSON.stringify({
                  appState,
                  selectedPackage: selectedPackage ? {
                    id: selectedPackage.recharge_product_id,
                    name: selectedPackage.recharge_product_name,
                    price: selectedPackage.cny_price
                  } : null,
                  linkData,
                  configLoaded: !!rechargeConfig,
                  totalPackages: rechargeConfig?.recharge_configs.length || 0
                }, null, 2)}
              </pre>
            </Card>
          </section>
        )}
        </SectionContainer>
      </PageContainer>
    </div>
  );
}

export default App;